import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next'
import { useRouter } from 'next/router'

import { withIronSessionSsr } from 'iron-session/next'
import invariant from 'tiny-invariant'

import { useFoodSWRInfinite } from '~/api-client/food_api'
import { userDinnerParams, userGetProfile } from '~/api-client/food_api/user'
import { DinnersDisclosure } from '~/components/food_app/dinner/DinnersDisclosure'
import { ChevronLeftIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { useAccessToken } from '~/provider/SessionProvider'
import { sessionOptions } from '~/session'

const Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props
) => {
  const accessToken = useAccessToken()
  const userOrganizingDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.organizing(props.userResponse.user.userId),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    }
  )

  const userAttendingDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.attending(props.userResponse.user.userId),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    }
  )

  const userAppliedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.applied(props.userResponse.user.userId),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    }
  )

  const userInvitedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.invited(props.userResponse.user.userId),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    }
  )

  const userOrganizedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.organized(props.userResponse.user.userId),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    }
  )

  const userAttendedDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.attended(props.userResponse.user.userId),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    }
  )
  const userSatWithMeDinners = useFoodSWRInfinite(
    '/user/dinners',
    userDinnerParams.satWithMe(props.userResponse.user.userId),
    (pageIndex) => {
      return {
        count: config.pageSize.userDinnerList,
        offset: pageIndex * config.pageSize.userDinnerList,
      }
    },
    {
      // 認証前はエラーになるので無効化
      disabled: accessToken == null,
    }
  )

  const router = useRouter()
  const { default_disclosure } = router.query

  return (
    <div>
      <Header>
        <ChevronLeftIcon fill="#fff" onClick={() => router.back()} />
        <div />
        食事会リスト
        <div />
        <div></div>
      </Header>
      <Container>
        <ContainerInset>
          <form>
            <div className="mt-4">
              {DinnersDisclosure({
                showParticipationStatus: props.isMe,
                titleText: '幹事の食事会',
                noContentText: '幹事の食事会はありません',
                paginatedDinners: userOrganizingDinners,
                defaultOpen: default_disclosure === 'organizing',
              })}
              {DinnersDisclosure({
                showParticipationStatus: props.isMe,
                titleText: '参加予定の食事会',
                noContentText: '参加予定の食事会はありません',
                paginatedDinners: userAttendingDinners,
                defaultOpen: default_disclosure === 'attending',
              })}
              {props.isMe &&
                DinnersDisclosure({
                  showParticipationStatus: props.isMe,
                  titleText: '応募中の食事会',
                  noContentText: '応募中の食事会はありません',
                  paginatedDinners: userAppliedDinners,
                  defaultOpen: default_disclosure === 'applied',
                })}
              {props.isMe &&
                DinnersDisclosure({
                  showParticipationStatus: props.isMe,
                  titleText: '誘われ中の食事会',
                  noContentText: '誘われ中の食事会はありません',
                  paginatedDinners: userInvitedDinners,
                  defaultOpen: default_disclosure === 'invited',
                })}
              {DinnersDisclosure({
                showParticipationStatus: props.isMe,
                titleText: '過去に幹事をした食事会',
                noContentText: '過去に幹事をした食事会はありません',
                paginatedDinners: userOrganizedDinners,
                defaultOpen: default_disclosure === 'organized',
              })}
              {DinnersDisclosure({
                showParticipationStatus: props.isMe,
                titleText: '過去に参加した食事会',
                noContentText: '過去に参加した食事会はありません',
                paginatedDinners: userAttendedDinners,
                defaultOpen: default_disclosure === 'attended',
              })}
              {DinnersDisclosure({
                showParticipationStatus: props.isMe,
                titleText: 'あなたと同席した食事会',
                noContentText: 'あなたと同席した食事会はありません',
                paginatedDinners: userSatWithMeDinners,
                defaultOpen: default_disclosure === 'sat_with_me',
              })}
            </div>
          </form>
        </ContainerInset>
      </Container>
      <Footer />
    </div>
  )
}

export const getServerSideProps = withIronSessionSsr(
  async (context: GetServerSidePropsContext) => {
    invariant(typeof context.query.user_name === 'string')

    const userResponse = await userGetProfile({
      accessToken: context.req.session.user?.accessToken,
      searchParams: {
        user_name: context.query.user_name,
      },
    })

    const props = {
      isMe:
        context.req.session.user?.userId != null &&
        context.req.session.user?.userId === userResponse.user.userId,
      userResponse: userResponse,
    }

    return { props }
  },
  sessionOptions
)

export default Page
